import React from 'react'
import moment from 'moment'
import { List, ListItem, ListItemText, makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { Announcement } from './announcementQueue'
import { isUnread } from './announcementQueue'

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    padding: theme.spacing(1.5, 2, 0.5),
    fontWeight: 600
  },
  empty: {
    padding: theme.spacing(1, 2, 2),
    color: theme.palette.text.secondary
  },
  unread: {
    fontWeight: 600
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flex: '0 0 8px',
    marginRight: theme.spacing(1.5),
    background: theme.palette.primary.main
  },
  dotRead: {
    background: 'transparent'
  }
}))

export interface AnnouncementHistoryListProps {
  /** `useAnnouncements().history`: newest first, unfired page-view ones already left out (#25610). */
  announcements: Announcement[]
  onSelect: (announcement: Announcement) => void
}

/**
 * The date-stamped announcement list with unread dots (#25346). Shared by the header's history
 * popover and the resource center panel (#25353).
 */
export default function AnnouncementHistoryList({ announcements, onSelect }: AnnouncementHistoryListProps) {
  const classes = useStyles()

  return (
    <>
      <Typography variant="subtitle1" className={classes.heading}>
        Announcements
      </Typography>
      {announcements.length === 0 ? (
        <Typography variant="body2" className={classes.empty}>
          No announcements yet.
        </Typography>
      ) : (
        <List dense>
          {announcements.map((announcement) => {
            const isNew = isUnread(announcement)
            return (
              <ListItem
                key={announcement.id}
                button
                onClick={() => onSelect(announcement)}
                data-testid={`announcement-history-item-${announcement.id}`}
              >
                <span className={`${classes.dot} ${isNew ? '' : classes.dotRead}`} aria-hidden />
                <ListItemText
                  primary={announcement.title}
                  secondary={moment(announcement.publishedAt).format('MMM D, YYYY')}
                  primaryTypographyProps={{ className: isNew ? classes.unread : undefined }}
                />
              </ListItem>
            )
          })}
        </List>
      )}
    </>
  )
}
